import {
  Box,
  Checkbox,
  Flex,
  Grid,
  GridItem,
  Highlight,
  Text,
} from '@chakra-ui/react';
import Link from 'next/link';
import React, { ChangeEvent, useCallback } from 'react';
import { theme } from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import Avatar from '~/components/Avatar';
import { CategoryTag } from '~/components/CategoryTag';
import { GenderTag } from '~/components/GenderTag';
import WebLinks from '~/components/WebLinks';
import { routes } from '~/core/router';
import { getMainPlatformData } from '~/core/utils';
import { useGetCreatorLocation } from '~/hooks/useGetCreatorLocation';
import { Platforms } from '~/screens/WhalarModeTalentRecord/components/Platforms';
import { ICreator } from '~/types';

import { useListsStore } from '../store/useSavedListStore';
import { CreatorButtons } from './CreatorButtons';
import { ShareCreatorButton } from './ShareCreatorButton';

export interface CreatorInfoDesktop {
  list: SavedList;
  creator: ICreator;
  note?: string;
  isProfile?: boolean;
  isShared?: boolean;
  onCheck?: (isChecked: boolean) => void;
  onNoteOpen?: () => void;
}

const stackNameLinkStyle = {
  lineHeight: '1',
  borderBottom: '2px solid transparent',
  transition: 'border-bottom 0.3s ease, margin-bottom 0.3s ease',
};

const stackNameLinkHoverStyle = {
  _hover: {
    borderBottom: `2px solid ${theme.colors.secondary[500]}`,
    marginBottom: '0px',
  },
};

// Copied from src/screens/MyAgency/components/StackView/CreatorInfo/CreatorInfoDesktop.tsx
export function CreatorInfo({
  list,
  creator,
  isProfile,
  isShared,
  note,
  onCheck,
  onNoteOpen,
}: CreatorInfoDesktop) {
  const { age, bioAgency, slug, webLinks } = creator;
  const { getLocation } = useGetCreatorLocation();
  const creatorLocation = getLocation(creator);
  const { avatar, name = '' } = getMainPlatformData(creator);

  const { creators } = useListsStore();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCheck?.(event.target.checked);
    },
    [onCheck],
  );

  return (
    <Grid
      templateColumns="repeat(4, 1fr)"
      display={{ base: 'none', lg: 'grid' }}
    >
      <GridItem rowSpan={2} colSpan={1} colEnd={1}>
        <Avatar
          width={32}
          height={32}
          marginRight={6}
          borderRadius="lg"
          src={avatar}
        />
      </GridItem>
      <GridItem colSpan={2}>
        <Flex>
          <Flex flexDirection="column" aria-label="Creator Info">
            {!isShared ? (
              <Link href={routes.profileOverview(creator.slug || '')}>
                <Box style={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <Text
                    fontSize="32px"
                    fontWeight="500"
                    color="primary.700"
                    whiteSpace="nowrap"
                    {...stackNameLinkStyle}
                    {...stackNameLinkHoverStyle}
                  >
                    <Highlight
                      query={'' /* TODO */}
                      styles={{ color: 'secondary.500' }}
                    >
                      {name}
                    </Highlight>
                  </Text>
                </Box>
              </Link>
            ) : (
              <Box>
                <Text
                  fontSize="32px"
                  fontWeight="500"
                  color="primary.700"
                  whiteSpace="nowrap"
                  {...stackNameLinkStyle}
                >
                  <Highlight
                    query={'' /* TODO */}
                    styles={{ color: 'secondary.500' }}
                  >
                    {name}
                  </Highlight>
                </Text>
              </Box>
            )}

            <Flex alignItems="center" gap={1}>
              {creatorLocation ? (
                <>
                  <Text fontWeight="400" fontSize="md" color="primary.700">
                    {creatorLocation}
                  </Text>
                </>
              ) : null}
              {age ? (
                <Text
                  fontWeight="400"
                  fontSize="md"
                  marginLeft={1}
                  color="primary.700"
                >
                  {age} years old
                </Text>
              ) : null}
            </Flex>
          </Flex>

          <Flex flexDirection="column">
            <Flex
              gap={3}
              alignItems="flex-start"
              marginLeft={10}
              aria-label="tags"
              marginTop={6}
            >
              <CategoryTag creator={creator} marginRight={4} />
              <GenderTag creator={creator} marginRight={6} />
            </Flex>
          </Flex>
        </Flex>
      </GridItem>

      <GridItem colSpan={2}>
        <Flex
          flexDirection="row"
          gap={4}
          justifyContent="flex-end"
          marginRight={6}
        >
          <CreatorButtons
            list={list}
            creator={creator}
            isShared={isShared}
            onNoteOpen={onNoteOpen}
            note={note}
          />
          {slug && !isShared && isProfile && creator ? (
            <ShareCreatorButton creator={creator} />
          ) : null}
          {onCheck && !isProfile && !isShared && (
            <Flex onClick={(event) => event.stopPropagation()}>
              <Checkbox
                isChecked={!!creators.find((c) => c.id === creator.id)}
                marginLeft={2}
                onChange={handleChange}
              />
            </Flex>
          )}
        </Flex>
      </GridItem>
      <GridItem colSpan={4}>
        <Flex gap={10}>
          <Flex alignItems="center">
            <Box minWidth={40}>
              <Platforms
                platforms={creator.platforms}
                isOffPlatform={creator.isOffPlatform}
                flexDirection="column"
                marginTop={1}
                view="stacked"
              />
              <Box marginTop={3}>
                <WebLinks links={webLinks} />
              </Box>
            </Box>
          </Flex>
          {bioAgency && (
            <Text fontSize="md" lineHeight={4}>
              {bioAgency}
            </Text>
          )}
        </Flex>
      </GridItem>
    </Grid>
  );
}
